/** Same-origin HTTPS proxy prefix used on Cloudflare Pages. */
export const HOSTED_API_BASE = "/api";

/** Public Pages host. The Capacitor shell cannot use a relative `/api` — that hits its own files. */
export const PAGES_ORIGIN = "https://northstar-companion.pages.dev";
export const PAGES_API_BASE = `${PAGES_ORIGIN}/api`;

export function companionApiBaseFromEnv(viteBase: string | undefined): string {
  const raw = (viteBase ?? "").trim().replace(/\/+$/, "");
  if (raw === "relative") return HOSTED_API_BASE;
  return raw;
}

export function isLocalHostname(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  return host === "localhost" || host === "127.0.0.1" || host === "[::1]" || host.endsWith(".local");
}

/**
 * Hosted phone build: HTTPS Pages (or an explicit Vite flag) talks to `/api`.
 * Local Vite / LAN stays on whatever URL the crew pastes (often plain HTTP Nanode).
 */
export function isHostedCompanionRuntime(
  viteBase: string | undefined = typeof import.meta !== "undefined"
    ? (import.meta as ImportMeta & { env?: { VITE_COMPANION_API_BASE?: string } }).env?.VITE_COMPANION_API_BASE
    : undefined,
  location?: { protocol: string; hostname: string } | null,
): boolean {
  if (companionApiBaseFromEnv(viteBase) === HOSTED_API_BASE) return true;
  const loc =
    location ?? (typeof window !== "undefined" ? { protocol: window.location.protocol, hostname: window.location.hostname } : null);
  if (!loc) return false;
  return loc.protocol === "https:" && !isLocalHostname(loc.hostname);
}

export function isCapacitorShell(location?: { protocol: string; hostname: string } | null): boolean {
  if (!location) return false;
  const host = location.hostname.trim().toLowerCase();
  if (location.protocol === "capacitor:") return host === "localhost" || host === "";
  return location.protocol === "https:" && host === "localhost";
}

export function currentPageLocation(): { protocol: string; hostname: string } | null {
  if (typeof window === "undefined") return null;
  return { protocol: window.location.protocol, hostname: window.location.hostname };
}

/**
 * Where account, sync, and weather calls go.
 * The PWA stays on same-origin `/api`. The Android shell uses the absolute Pages proxy.
 */
export function companionRuntimeApiBase(
  viteBase?: string,
  location?: { protocol: string; hostname: string } | null,
): string {
  const loc = location === undefined ? currentPageLocation() : location;
  if (isCapacitorShell(loc)) return PAGES_API_BASE;
  if (isHostedCompanionRuntime(viteBase, loc)) return HOSTED_API_BASE;
  return "";
}

export function isHostedCompanionApiBase(baseUrl: string): boolean {
  const normalized = baseUrl.trim().replace(/\/+$/, "");
  return normalized === HOSTED_API_BASE || normalized === PAGES_API_BASE;
}

export function resolveCompanionSyncBase(settingsBase: string, hosted: boolean): string {
  const n = settingsBase.trim().replace(/\/+$/, "");
  if (n) return n;
  return hosted ? HOSTED_API_BASE : "";
}
